import React, {useEffect, useState} from 'react';
import {View} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import FormHeader from './FormHeader';
import DynamicPage from './DynamicPage';
import PageControlFooter from './PageControlFooter';
import LoadingSpinner from '../Shared/LoadingSpinner';
import {ObjectId} from 'bson';
import {
  TemplateVersion,
  TemplateVersion_conditions,
  SubmissionBundle_submissions,
} from '../../Models/PowerSyncModels/models';
import {SubmissionAnswerDTO} from '../../Types/DtoTypes';
import {DynamicPageStyleSheet} from '../../Styles/DynamicPageStyles';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import Colors from '../../Styles/Shared/Colors';
import GeneralHelperService from '../../Services/GeneralHelperService';
import { IGeneralHelperService } from '../../Services/Interfaces/IGeneralHelperService';

type BundleFormProps = {
  navigation: any;
  bundleId: string | null;
  submissionId: string | null;
  submissionType?: 'submission' | 'submissionTraining';
  headerTitle?: string;
  externalView?: boolean;
};

const BundleForm = (props: BundleFormProps): React.ReactElement => {
  //Leave dynamic form if props aren't populated
  if (!props.bundleId || !props.submissionId) {
    console.error('Invalid dynamic form props');
    props.navigation.pop();
  }

  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setPage] = useState(0);
  const [form, setForm] = useState<TemplateVersion | null>(null);
  const [submission, setSubmission] =
    useState<SubmissionBundle_submissions | null>(null);
  const [answers, setAnswers] = useState<SubmissionAnswerDTO[]>([]);
  const {templates, submissionBundles, templateVersions} = usePowerSyncData();

  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState('');

  const helperService: IGeneralHelperService = new GeneralHelperService();

  useEffect(() => {
    const getForm = async () => {
      let bundle = submissionBundles.find(b => b._id === props.bundleId!);

      if (bundle && bundle.submissions) {
        let sub = bundle.submissions.find(
          s => s.submissionId && s.submissionId === props.submissionId!,
        );
        if (sub) {
          let formVersion = templateVersions.find(
            f => f._id === sub?.templateVersion!._id!,
          )!;

          if (formVersion) {
            let template = templates.find(
              x => x._id === formVersion.templateId,
            )!;
            if (template && template.iconSvg)
              setIcon(helperService.getTemplateIcon(template.iconSvg, Colors.white, Colors.green));

            //Store answers so its not querying realm every time
            let answers: SubmissionAnswerDTO[] = sub.answers.map(a => ({
              controlId: a.controlId,
              answer: a.answer,
            }));

            setForm(formVersion);
            setSubmission(sub);
            setAnswers(answers);
            setTitle(sub.templateVersion?.name ?? '');
          }
        }
      }
    };

    getForm();
  }, [props.submissionId]);

  const goToPage = (pageNumber: number) => {
    if (
      pageNumber != currentPage &&
      pageNumber >= 0 &&
      pageNumber < form?.pages.length!
    )
      setPage(pageNumber);
  };

  const getPreviousPage = () => {
    if (currentPage > 0) setPage(currentPage - 1);
  };

  const getNextPage = () => {
    if (currentPage != form?.pages.length! - 1) setPage(currentPage + 1);
  };

  const onClose = async () => {
    props.navigation.pop();
  };

  // If form is not loaded yet show a Loading Spinner
  if (!form || !submission) {
    return <LoadingSpinner message="Loading" visible={true} />;
  }

  let conditions: TemplateVersion_conditions[] = form.conditions ?? [];
  let page = form.pages[currentPage];
  if (!page.controls || page.controls.length == 0) {
    console.error('Page missing controls');
    props.navigation.pop();
  }

  return (
    <View style={DynamicPageStyleSheet.bodyContainer}>
      <FormHeader
        submissionId={submission?.submissionId ?? ''}
        submissionType={props.submissionType}
        submissionStatus={
          props.submissionType === 'submission'
            ? SubmissionStatuses.SUBMITTED
            : SubmissionStatuses.COMPLETE
        }
        formName={props.headerTitle ?? form.displayName ?? form.name}
        formIcon={icon}
        pageCount={form.pages.length}
        currentPage={currentPage}
        pagePressed={goToPage}
        closePressed={onClose}
        validations={[]}
        showErrors={form.pages.map((p, i) => ({page: i, show: false}))}
        showMoreActions={props.externalView ? !props.externalView : true}
      />
      <LoadingSpinner message="Loading" visible={isLoading} />
      <DynamicPage
        navigation={props.navigation}
        submissionId={submission!.submissionId!}
        submissionSQLServerId={submission!.submissionSQLServerId ?? ''}
        submitForm={() => {}}
        answers={answers}
        page={page}
        pageNumber={currentPage}
        conditions={conditions}
        disabled={true}
        setIsLoading={(loading: boolean) => {
          setIsLoading(loading);
        }}
        validations={[]}
        showErrors={false}
        externalView={props.externalView}
        formVersion={form.version}
      />
      <PageControlFooter
        backOnPress={getPreviousPage}
        nextOnPress={getNextPage}
        currentPage={currentPage}
        maxPages={form.pages.length}
      />
    </View>
  );
};

export default BundleForm;
